import { useCallback, useContext, useMemo, useRef, useEffect, SyntheticEvent } from 'react';

import type { LinkingOptions } from '@atlaskit/editor-common/types';

import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';
import { smartCardEventHandler } from '@confluence/linking-platform';
import { RoutesContext } from '@confluence/route-manager';

import { useLinkingOptions } from '../hooks';

type AkEditorLinkingProp = { linking: LinkingOptions };

type CardOptions = {
	allowEmbeds: boolean;
	allowBlockCards: boolean;
	allowDatasource: boolean;
};
interface EditorLinkingProps {
	children: (props: AkEditorLinkingProp) => React.ReactElement<any, any> | null;
	options?: Partial<CardOptions>;
	viewMode?: boolean;
	isChromelessEditor?: boolean;
	isLivePage?: boolean;
}

export const useLinkingFeatures = (
	options?: Partial<CardOptions>,
	viewMode?: boolean,
	isChromelessEditor?: boolean,
	isLivePage?: boolean,
): AkEditorLinkingProp => {
	const config = useLinkingOptions(options, isChromelessEditor);
	const { cloudId } = useSessionData();
	// TODO MODES-4363 Once getSpaTransitionHref & its consumers are refactored to not use RoutesContext, use RouteState instead of RoutesContext
	const routesContext = useContext(RoutesContext);

	const productConfig = useMemo(
		() => ({
			product: 'confluence',
			cloudId,
		}),
		[cloudId],
	);

	/**
	 * Store live page state props in refs so the onClickCallback has a stable reference
	 * The onClickCallback passed to editor (and consumed within the useUniversalPreset hook)
	 * is not updated when the live page state changes.
	 */
	const isLivePageRef = useRef(isLivePage);
	const viewModeRef = useRef(viewMode);

	useEffect(() => {
		isLivePageRef.current = isLivePage;
		viewModeRef.current = viewMode;
	}, [isLivePage, viewMode]);

	/**
	 * By passing a callback we circumvent the default link click handling within platform
	 *
	 * We only want to allow smart links in live edit mode to navigte when this platform FF
	 * is enabled. The guarded behaviour can be instilled as the default on clean up (alongside
	 * removing the viewMode prop).
	 */

	const onClickCallback = useCallback(
		({ event, url }: { event: SyntheticEvent<HTMLAnchorElement>; url?: string }) => {
			if (fg('linking_platform_smart_links_in_live_pages')) {
				if (isLivePageRef.current && url) {
					smartCardEventHandler(routesContext, event, url);
				}
			} else {
				if (viewModeRef.current && url) {
					smartCardEventHandler(routesContext, event, url);
				}
			}
		},
		[routesContext],
	);

	const linkPickerProps = useLinkPickerEditorProps(undefined, productConfig);

	return useMemo<AkEditorLinkingProp>(
		() => ({
			linking: {
				...config.linking,
				linkPicker: {
					...config.linking?.linkPicker,
					...linkPickerProps,
				},
				smartLinks: {
					...config.linking?.smartLinks,
					onClickCallback,
				},
			},
		}),
		[config, linkPickerProps, onClickCallback],
	);
};

/**
 * Component that provides linking features to editor via props
 */
export const EditorLinkingFeatures = ({
	children,
	options,
	viewMode = false,
	isChromelessEditor = false,
	isLivePage = false,
}: EditorLinkingProps) => {
	const mergedProps = useLinkingFeatures(options, viewMode, isChromelessEditor, isLivePage);
	return children(mergedProps);
};
